export type Identifier = string | number;

export interface TeamDetail {
  id: Identifier;
  name: string;
  avatar?: string | null;
  description?: string | null;
  user_id: Identifier;
  founder_username?: string | null;
  founder_nickname?: string | null;
  founder_mobile_masked?: string | null;
  founder_email_masked?: string | null;
  founder_status?: number | null;
  total_credits: number;
  frozen_credits: number;
  used_credits: number;
  stored_credits: number;
  member_count: number;
  max_members: number;
  project_count: number;
  subscription_status: string;
  subscription_id?: Identifier | null;
  status: number;
  create_time?: string | null;
  update_time?: string | null;
}

export interface TeamMemberRow {
  id: Identifier;
  team_id: Identifier;
  user_id: Identifier;
  user_username?: string | null;
  user_nickname?: string | null;
  user_mobile_masked?: string | null;
  user_email_masked?: string | null;
  user_avatar?: string | null;
  user_status?: number | null;
  role: string;
  nickname?: string | null;
  avatar?: string | null;
  joined_at?: string | null;
  status: number;
  credit_id?: Identifier | null;
  allocated_credits: number;
  member_used_credits: number;
  available_credits: number;
  monthly_used: number;
  monthly_limit?: number | null;
  credit_status?: string | null;
  create_time?: string | null;
  update_time?: string | null;
}

export interface TeamSubscriptionRow {
  id: Identifier;
  team_id: Identifier;
  plan_id: Identifier;
  plan_name?: string | null;
  billing_cycle: string;
  status: string;
  price: number;
  total_price: number;
  member_count: number;
  credits_per_period: number;
  max_members: number;
  max_credits_stored: number;
  started_at?: string | null;
  current_period_start?: string | null;
  current_period_end?: string | null;
  total_credits_earned: number;
  total_credits_used: number;
  credits_per_month: number;
  months_issued: number;
  previous_plan_id?: Identifier | null;
  expired_at?: string | null;
  last_reminder_sent_at?: string | null;
  reminder_days_before_expiry?: number | null;
  is_current: boolean;
  days_until_expiry: number;
  create_time?: string | null;
  update_time?: string | null;
}

export interface TeamSubscriptionPaymentRow {
  id: Identifier;
  subscription_id: Identifier;
  team_id: Identifier;
  payment_no: string;
  amount: number;
  member_count: number;
  billing_cycle: string;
  credits_added: number;
  type?: string | null;
  type_name?: string | null;
  package_days?: number | null;
  point?: number | null;
  content?: string | null;
  vip_package_name?: string | null;
  vip_package_price?: number | null;
  status: string;
  pay_method?: string | null;
  trade_no?: string | null;
  user_id: Identifier;
  deducted_from_balance: number;
  error_code?: string | null;
  error_message?: string | null;
  paid_time?: string | null;
  create_time?: string | null;
}

export interface TeamCreditSummary {
  id: Identifier;
  team_id: Identifier;
  total_credits: number;
  frozen_credits: number;
  used_credits: number;
  stored_credits: number;
  members_with_credit: number;
  member_allocated_credits: number;
  member_used_credits: number;
  member_available_credits: number;
}

export interface TeamCreditTransactionRow {
  id: Identifier;
  team_id: Identifier;
  user_id?: Identifier | null;
  user_nickname?: string | null;
  user_mobile_masked?: string | null;
  project_id?: Identifier | null;
  project_name?: string | null;
  task_id?: string | null;
  item_id?: string | null;
  type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  idempotency_key?: string | null;
  description?: string | null;
  subscription_id?: Identifier | null;
  allocation_id?: Identifier | null;
  create_time?: string | null;
}

export interface TeamProjectRow {
  id: Identifier;
  name: string;
  cover?: string | null;
  description?: string | null;
  folder_id?: Identifier | null;
  owner_user_id: Identifier;
  owner_username?: string | null;
  owner_nickname?: string | null;
  owner_mobile_masked?: string | null;
  team_project_id: Identifier;
  team_id: Identifier;
  team_project_status: string;
  team_folder_id?: Identifier | null;
  created_by: Identifier;
  canvas_count: number;
  task_count: number;
  created_at?: string | null;
  updated_at?: string | null;
  team_project_create_time?: string | null;
  team_project_update_time?: string | null;
}

export const subscriptionStatusLabels: Record<string, string> = {
  active: '有效',
  inactive: '未开通',
  expired: '已过期',
  paused: '已暂停',
  cancelled: '已取消',
  canceled: '已取消',
};

export const billingCycleLabels: Record<string, string> = {
  monthly: '月度',
  quarterly: '季度',
  yearly: '年度',
};

export const teamMemberRoleLabels: Record<string, string> = {
  founder: '创始人',
  admin: '管理员',
  member: '普通成员',
};

export const creditStatusLabels: Record<string, string> = {
  active: '正常',
  frozen: '已冻结',
  released: '已释放',
};

export const creditTransactionTypeLabels: Record<string, string> = {
  subscription_credit: '套餐发放',
  allocated: '分配给成员',
  reclaimed: '回收成员积分',
  used: '团队消耗',
  member_consumed: '成员消耗',
  released: '成员移除解冻',
  team_dissolved: '团队解散返还',
  manual_add: '后台补充',
  refunded: '失败退回',
};

export const paymentMethodLabels: Record<string, string> = {
  balance: '账户余额',
  alipay: '支付宝',
  wechat: '微信支付',
  admin: '后台开通',
};

export const paymentStatusLabels: Record<string, string> = {
  pending: '待支付',
  completed: '已完成',
  failed: '失败',
  refunded: '已退款',
};
